import { type FC } from "react";

import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import type { FlowQuestion, FlowAnswers, FlowInputField } from "@/shared/constants/flowTypes";
import { formatPhoneInput } from "@/shared/utils/formatters";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";

interface ValidationErrors {
  [fieldId: string]: string;
}

interface InputFieldProps {
  question: FlowQuestion;
  values: FlowAnswers;
  errors: ValidationErrors;
  onChange: (fieldId: string, value: string) => void;
}

export const InputField: FC<InputFieldProps> = ({ question, values, errors, onChange }) => {
  if (!question.fields) return null;

  const handleChange = (field: FlowInputField, inputValue: string) => {
    if (field.inputType === "tel") {
      const formatted = formatPhoneInput(inputValue);
      onChange(field.id, formatted);
    } else {
      onChange(field.id, inputValue);
    }
  };

  const renderField = (field: FlowInputField) => {
    const hasError = !!errors[field.id];
    return (
      <div key={field.id} className="flex flex-1 flex-col gap-1.5">
        {field.label && (
          <label htmlFor={field.id} className="text-sm font-medium text-primary">
            {field.label}
            {!field.required && <span className="font-normal text-tertiary"> (optional)</span>}
            {field.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipInfoTrigger
                    aria-label={field.tooltip.title}
                    className="ml-1 align-middle"
                  />
                  <TooltipContent title={field.tooltip.title}>
                    {field.tooltip.content}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </label>
        )}
        <Input
          id={field.id}
          type={field.inputType === "tel" ? "text" : (field.inputType ?? "text")}
          placeholder={field.placeholder}
          value={(values[field.id] as string) ?? ""}
          onChange={(e) => handleChange(field, e.target.value)}
          className={hasError ? "border-destructive" : undefined}
          style={hasError ? { borderWidth: "2px" } : undefined}
        />
        <FieldError message={errors[field.id]} />
      </div>
    );
  };

  const MAX_INLINE_COLS = 2;
  const rows: FlowInputField[][] = [];
  for (const field of question.fields) {
    const lastRow = rows[rows.length - 1];
    if (field.inline && lastRow && lastRow.length < MAX_INLINE_COLS && lastRow[0]?.inline) {
      lastRow.push(field);
    } else {
      rows.push([field]);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {rows.map((row) =>
        row.length > 1 ? (
          <div key={row.map((f) => f.id).join("-")} className="flex gap-3">
            {row.map(renderField)}
          </div>
        ) : (
          renderField(row[0])
        ),
      )}
    </div>
  );
};
